// Broadnet → Conexión: modo (simulado|produccion), URL/credenciales del WebService,
// FTP de conciliación, timeouts, comisión por defecto y reintentos.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Plug, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { BnConfigFull, Campo, Switch, bnConfigGet, bnConfigSet, fieldCls } from './shared';

const vacio = {
  modo: 'simulado', url_webservice: '', webservice_usuario: '', webservice_clave: '',
  host: '', ftp_host: '', ftp_usuario: '', ftp_ruta: '', ftp_clave: '',
  timeout_general_seg: '40', timeout_recarga_seg: '2', timeout_sb_seg: '10',
  comision_default_pct: '0', reintentos_recarga: '3', reintentos_sb: '5', es_activo: true,
  recarga_facturacion: 'broadnet' as 'broadnet' | 'comercio', recarga_iva_tarifa: '0',
  gestion_modo: 'ninguna' as 'ninguna' | 'fija' | 'rangos', gestion_valor: '0',
  gestion_iva_pct: '0', gestion_captura: 'total' as 'total' | 'recarga',
};

export function ConexionBroadnet() {
  const qc = useQueryClient();
  const { data: cfg } = useQuery<BnConfigFull>({ queryKey: ['pos-bn-config-full'], queryFn: bnConfigGet, staleTime: 0 });

  const [f, setF] = useState<typeof vacio>(vacio);
  const set = (patch: Partial<typeof vacio>) => setF((s) => ({ ...s, ...patch }));
  useEffect(() => {
    if (cfg) setF({
      modo: cfg.modo, url_webservice: cfg.url_webservice ?? '', webservice_usuario: cfg.webservice_usuario ?? '', webservice_clave: '',
      host: cfg.host ?? '', ftp_host: cfg.ftp_host ?? '', ftp_usuario: cfg.ftp_usuario ?? '', ftp_ruta: cfg.ftp_ruta ?? '', ftp_clave: '',
      timeout_general_seg: String(cfg.timeout_general_seg), timeout_recarga_seg: String(cfg.timeout_recarga_seg), timeout_sb_seg: String(cfg.timeout_sb_seg),
      comision_default_pct: String(cfg.comision_default_pct), reintentos_recarga: String(cfg.reintentos_recarga), reintentos_sb: String(cfg.reintentos_sb),
      es_activo: cfg.es_activo,
      recarga_facturacion: cfg.recarga_facturacion ?? 'broadnet', recarga_iva_tarifa: String(cfg.recarga_iva_tarifa ?? 0),
      gestion_modo: cfg.gestion_modo ?? 'ninguna', gestion_valor: String(cfg.gestion_valor ?? 0),
      gestion_iva_pct: String(cfg.gestion_iva_pct ?? 0), gestion_captura: cfg.gestion_captura ?? 'total',
    });
  }, [cfg]);

  const guardar = useMutation({
    mutationFn: () => bnConfigSet({
      modo: f.modo, url_webservice: f.url_webservice || null, webservice_usuario: f.webservice_usuario || null,
      webservice_clave: f.webservice_clave || '', // '' = conservar
      host: f.host || null, ftp_host: f.ftp_host || null, ftp_usuario: f.ftp_usuario || null, ftp_ruta: f.ftp_ruta || null,
      ftp_clave: f.ftp_clave || '', // '' = conservar
      timeout_general_seg: Number(f.timeout_general_seg) || 40,
      timeout_recarga_seg: Number(f.timeout_recarga_seg) || 2,
      timeout_sb_seg: Number(f.timeout_sb_seg) || 10,
      comision_default_pct: Number(f.comision_default_pct) || 0,
      reintentos_recarga: Number(f.reintentos_recarga) || 3,
      reintentos_sb: Number(f.reintentos_sb) || 5,
      es_activo: f.es_activo,
      recarga_facturacion: f.recarga_facturacion,
      recarga_iva_tarifa: Number(f.recarga_iva_tarifa) || 0,
      gestion_modo: f.gestion_modo,
      gestion_valor: Number(f.gestion_valor) || 0,
      gestion_iva_pct: Number(f.gestion_iva_pct) || 0,
      gestion_captura: f.gestion_captura,
    }),
    onSuccess: () => {
      toast.success('Conexión Broadnet guardada');
      set({ webservice_clave: '', ftp_clave: '' });
      void qc.invalidateQueries({ queryKey: ['pos-bn-config-full'] });
      void qc.invalidateQueries({ queryKey: ['pos-bn-config'] }); // caché de Recargas.tsx (Fase 1)
      void qc.invalidateQueries({ queryKey: ['pos-bn-config-recarga'] }); // caché de PuntoVenta.tsx (Fase 5)
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Plug className="w-5 h-5" /> Broadnet · Conexión</h1>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Modo</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Campo label="Modo de operación" className="max-w-sm">
            <select className={fieldCls} value={f.modo} onChange={(e) => set({ modo: e.target.value })}>
              <option value="simulado">Simulado (pruebas)</option>
              <option value="produccion">WebService (producción)</option>
            </select>
          </Campo>
          {f.modo === 'simulado' ? (
            <p className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              En modo <span className="font-medium">Simulado</span> no se conecta a Broadnet real: las recargas se resuelven
              con reglas deterministas (para probar el flujo completo sin afectar saldo real). Cambia a
              <span className="font-medium"> WebService</span> cuando tengas las credenciales de producción.
            </p>
          ) : (
            <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Modo producción: cada recarga llama al WebService real de Broadnet con las credenciales de abajo.
            </p>
          )}
          <Switch checked={f.es_activo} onChange={(v) => set({ es_activo: v })} label="Recargas activas en el POS" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">WebService</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Campo label="URL del WebService">
            <input className={fieldCls} value={f.url_webservice} onChange={(e) => set({ url_webservice: e.target.value })} placeholder="https://…/broadnet/ws" />
          </Campo>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Campo label="Usuario">
              <input className={fieldCls} value={f.webservice_usuario} onChange={(e) => set({ webservice_usuario: e.target.value })} />
            </Campo>
            <Campo label={`Clave ${cfg?.webservice_clave_configurada ? '· ✓ configurada (dejar en blanco = conservar)' : ''}`}>
              <input type="password" autoComplete="new-password" className={fieldCls}
                placeholder={cfg?.webservice_clave_configurada ? '•••• (configurada)' : ''}
                value={f.webservice_clave} onChange={(e) => set({ webservice_clave: e.target.value })} />
            </Campo>
          </div>
          <Campo label="Host / VPN (referencia)">
            <input className={fieldCls} value={f.host} onChange={(e) => set({ host: e.target.value })} placeholder="IP o alias de la VPN del switch Broadnet" />
          </Campo>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">FTP de conciliación</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Campo label="Host FTP"><input className={fieldCls} value={f.ftp_host} onChange={(e) => set({ ftp_host: e.target.value })} /></Campo>
            <Campo label="Usuario FTP"><input className={fieldCls} value={f.ftp_usuario} onChange={(e) => set({ ftp_usuario: e.target.value })} /></Campo>
            <Campo label={`Clave FTP ${cfg?.ftp_clave_configurada ? '· ✓ configurada (dejar en blanco = conservar)' : ''}`}>
              <input type="password" autoComplete="new-password" className={fieldCls}
                placeholder={cfg?.ftp_clave_configurada ? '•••• (configurada)' : ''}
                value={f.ftp_clave} onChange={(e) => set({ ftp_clave: e.target.value })} />
            </Campo>
          </div>
          <Campo label="Ruta remota (donde deja el Excel diario)">
            <input className={fieldCls} value={f.ftp_ruta} onChange={(e) => set({ ftp_ruta: e.target.value })} placeholder="/conciliacion/diario" />
          </Campo>
          <p className="text-xs text-muted-foreground">
            El Excel diario de Broadnet se cruza manualmente contra Monitoreo (Referencia + Autorización) — la descarga automática vía FTP es fase futura.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Timeouts, comisión y reintentos</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Campo label="Timeout general (seg)">
              <input type="number" min={1} className={fieldCls} value={f.timeout_general_seg} onChange={(e) => set({ timeout_general_seg: e.target.value })} />
            </Campo>
            <Campo label="Timeout recarga (seg)">
              <input type="number" min={1} className={fieldCls} value={f.timeout_recarga_seg} onChange={(e) => set({ timeout_recarga_seg: e.target.value })} />
            </Campo>
            <Campo label="Timeout servicio básico (seg)">
              <input type="number" min={1} className={fieldCls} value={f.timeout_sb_seg} onChange={(e) => set({ timeout_sb_seg: e.target.value })} />
            </Campo>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Campo label="Comisión por defecto (%)">
              <input type="number" min={0} step="0.001" className={fieldCls} value={f.comision_default_pct} onChange={(e) => set({ comision_default_pct: e.target.value })} />
            </Campo>
            <Campo label="Reintentos recarga">
              <input type="number" min={0} className={fieldCls} value={f.reintentos_recarga} onChange={(e) => set({ reintentos_recarga: e.target.value })} />
            </Campo>
            <Campo label="Reintentos servicio básico">
              <input type="number" min={0} className={fieldCls} value={f.reintentos_sb} onChange={(e) => set({ reintentos_sb: e.target.value })} />
            </Campo>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Comisión de gestión (lo que el comercio cobra al cliente)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Es <b>adicional</b> a la comisión que Broadnet le paga al comercio, y va en dirección
            contraria: ésta la paga el cliente. En una recarga de 1,05 con 0,05 de gestión, el cliente
            paga 1,10, se recargan 1,05, y la factura sale con <b>las dos líneas desglosadas</b>.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Campo label="Cómo se cobra">
              <select className={fieldCls} value={f.gestion_modo}
                onChange={(e) => set({ gestion_modo: e.target.value as typeof f.gestion_modo })}>
                <option value="ninguna">No se cobra</option>
                <option value="fija">Valor fijo</option>
                <option value="rangos">Por rango de monto</option>
              </select>
            </Campo>
            <Campo label="Valor ($)">
              <input type="number" min={0} step="0.01" className={fieldCls} disabled={f.gestion_modo !== 'fija'}
                value={f.gestion_valor} onChange={(e) => set({ gestion_valor: e.target.value })} />
            </Campo>
            <Campo label="IVA de la comisión (%)">
              <input type="number" min={0} step="0.01" className={fieldCls} disabled={f.gestion_modo === 'ninguna'}
                value={f.gestion_iva_pct} onChange={(e) => set({ gestion_iva_pct: e.target.value })} />
            </Campo>
            <Campo label="Qué teclea el cajero">
              <select className={fieldCls} disabled={f.gestion_modo === 'ninguna'} value={f.gestion_captura}
                onChange={(e) => set({ gestion_captura: e.target.value as typeof f.gestion_captura })}>
                <option value="total">Lo que paga el cliente (1,10)</option>
                <option value="recarga">Lo que se va a recargar (1,05)</option>
              </select>
            </Campo>
          </div>
          {f.gestion_modo !== 'ninguna' && (
            <div className="rounded-md bg-muted p-3 text-xs text-muted-foreground space-y-1">
              <p>
                <b>IVA:</b> 0 = exenta. Si pones un porcentaje, el valor de arriba se entiende
                <b> con IVA incluido</b> — el cajero cobra esa cifra exacta y la factura la desglosa.
              </p>
              <p>
                <b>En los recibos de servicios la comisión siempre se SUMA</b>, sea cual sea el modo:
                al valor que cobra la empresa eléctrica no se le puede restar nada. Y sólo la comisión
                es ingreso del comercio — el valor del recibo pasa por caja, no es venta.
              </p>
              <p>Cada operadora puede llevar la suya en <b>Productos</b>; ésta es la que se usa si dice «heredar».</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Facturación de recargas (valor por defecto)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Esto es el <b>valor por defecto para las recargas</b>. Cada operadora puede decir otra cosa
            en <b>Productos</b> → columna «¿Quién factura?». Los servicios básicos van siempre con recibo
            salvo que allí se marque lo contrario, porque una recaudación no es una venta.
          </p>
          <Campo label="¿Quién factura las recargas?" className="max-w-md">
            <select className={fieldCls} value={f.recarga_facturacion} onChange={(e) => set({ recarga_facturacion: e.target.value as 'broadnet' | 'comercio' })}>
              <option value="broadnet">Broadnet factura — el comercio da recibo</option>
              <option value="comercio">El comercio emite factura SRI</option>
            </select>
          </Campo>
          {f.recarga_facturacion === 'broadnet' ? (
            <p className="text-xs text-muted-foreground">
              La recarga se registra con el voucher/recibo interno (NO tributario) de siempre — Broadnet emite su propia nota de venta.
            </p>
          ) : (
            <div className="space-y-2">
              <Campo label="IVA de la recarga (%)" className="max-w-xs">
                <input type="number" min={0} step="0.01" className={fieldCls} value={f.recarga_iva_tarifa} onChange={(e) => set({ recarga_iva_tarifa: e.target.value })} />
              </Campo>
              <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                Consúltalo con tu contador; por defecto 0. Al vender una recarga se emitirá una FACTURA SRI (no el voucher) sobre un producto de
                servicio POR OPERADORA (p. ej. "Recarga CLARO", código REC-05), que se crea solo la primera vez — así tus reportes de ventas y
                contabilidad se desglosan por operadora.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Guardar</Button>
      </div>
    </div>
  );
}
